import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { money, centsToInput, parseMoneyToCents } from "../lib/format";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const DCATS = [
  { v: "lab", l: "Laboratory fees" }, { v: "materials", l: "Materials" },
  { v: "other", l: "Other deduction" }, { v: "custom", l: "Custom" },
];

export default function EditPaymentReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: r, isLoading } = useQuery({
    queryKey: ["payment-report", id],
    queryFn: async () => (await api.get(`/payment-reports/${id}`)).data,
  });

  const [form, setForm] = useState(null);
  const [deductions, setDeductions] = useState(null);
  const [saving, setSaving] = useState(false);

  if (isLoading || !r) {
    return <AppShell hideNav><PageHeader title="Edit Report" onBack={() => navigate(-1)} /><div className="px-5 text-slate-400">Loading…</div></AppShell>;
  }

  const f = form || {
    period_start: r.period_start, period_end: r.period_end,
    report_date: r.report_date || r.period_end, payment_date: r.payment_date || "",
    total_billing: centsToInput(r.total_billing_cents),
    actual: r.actual_cents == null ? "" : centsToInput(r.actual_cents),
    notes: r.notes || "", invoice_reference: r.invoice_reference || "",
  };
  const ded = deductions || (r.deductions || []).map((d) => ({ category: d.category, amount: centsToInput(d.amount_cents) }));
  const set = (k, v) => setForm({ ...f, [k]: v });
  const setDed = (i, k, v) => setDeductions(ded.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addDed = () => setDeductions([...ded, { category: "other", amount: "" }]);
  const rmDed = (i) => setDeductions(ded.filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api.put(`/payment-reports/${id}`, {
        practice_id: r.practice_id,
        period_start: f.period_start, period_end: f.period_end,
        report_date: f.report_date || f.period_end, payment_date: f.payment_date || null,
        invoice_reference: f.invoice_reference || null,
        total_billing_cents: parseMoneyToCents(f.total_billing),
        deductions: ded.filter((d) => d.amount !== "").map((d) => ({ category: d.category, amount_cents: parseMoneyToCents(d.amount) })),
        actual_cents: f.actual === "" ? null : parseMoneyToCents(f.actual),
        notes: f.notes,
      });
      qc.invalidateQueries();
      toast.success("Payment report updated");
      navigate(`/money/report/${id}`);
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <AppShell>
      <PageHeader title="Edit Report" subtitle={r.practice_name} onBack={() => navigate(`/money/report/${id}`)} />
      <div className="px-5 lg:px-8 pb-44 df-fade-in space-y-4">
        <div className="df-card p-4 space-y-4">
          <div className="rounded-xl bg-[#E6F4F1] px-3 py-2 text-xs text-[#0A6055]" data-testid="edit-rule-note">
            Recalculated using this report's saved agreement{r.agreement_version ? ` (version ${r.agreement_version})` : ""} — later agreement changes won't affect it.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-700">Period start</Label><Input type="date" data-testid="edit-period-start" value={f.period_start} onChange={(e) => set("period_start", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
            <div><Label className="text-slate-700">Period end</Label><Input type="date" data-testid="edit-period-end" value={f.period_end} onChange={(e) => set("period_end", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
            <div><Label className="text-slate-700">Report date</Label><Input type="date" data-testid="edit-report-date" value={f.report_date} onChange={(e) => set("report_date", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
            <div><Label className="text-slate-700">Payment date</Label><Input type="date" data-testid="edit-payment-date" value={f.payment_date} onChange={(e) => set("payment_date", e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" /></div>
          </div>
          <div>
            <Label className="text-slate-700">Total billing</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
              <Input type="number" step="0.01" inputMode="decimal" data-testid="edit-total-billing" value={f.total_billing} onChange={(e) => set("total_billing", e.target.value)} placeholder="0.00" className="h-12 rounded-xl bg-white pl-7 font-num" />
            </div>
          </div>
          <div>
            <Label className="text-slate-700">Invoice / reference (optional)</Label>
            <Input data-testid="edit-invoice-reference" value={f.invoice_reference} onChange={(e) => set("invoice_reference", e.target.value)} placeholder="e.g. INV-202601" className="mt-1.5 h-12 rounded-xl bg-white" />
          </div>
        </div>

        <div className="df-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-900">Deductions</div>
            <button data-testid="edit-add-deduction" onClick={addDed} className="text-sm text-[#0D786A] font-medium df-pressable flex items-center gap-1"><Plus size={14} /> Add</button>
          </div>
          {ded.map((d, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`edit-deduction-row-${i}`}>
              <Select value={d.category} onValueChange={(v) => setDed(i, "category", v)}>
                <SelectTrigger className="h-11 rounded-xl bg-white flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>{DCATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
              <div className="relative w-32">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-num text-sm">$</span>
                <Input type="number" step="0.01" inputMode="decimal" data-testid={`edit-deduction-amount-${i}`} value={d.amount} onChange={(e) => setDed(i, "amount", e.target.value)} placeholder="0.00" className="h-11 rounded-xl bg-white pl-6 font-num text-sm" />
              </div>
              <button onClick={() => rmDed(i)} className="text-slate-400 df-pressable p-1"><X size={16} /></button>
            </div>
          ))}
          {ded.length === 0 && <div className="text-sm text-slate-400">No deductions.</div>}
        </div>

        <div className="df-card p-4 space-y-3">
          <div>
            <Label className="text-slate-700">Actual payment received (optional)</Label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
              <Input type="number" step="0.01" inputMode="decimal" data-testid="edit-actual" value={f.actual} onChange={(e) => set("actual", e.target.value)} placeholder="Leave blank if awaiting" className="h-12 rounded-xl bg-white pl-7 font-num" />
            </div>
          </div>
          <div><Label className="text-slate-700">Notes (optional)</Label><Textarea data-testid="edit-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} className="mt-1.5 rounded-xl bg-white" /></div>
          <p className="text-[11px] text-slate-400">Current expected payment: <b className="font-num">{money(r.expected_cents)}</b>. Expected recalculates when you change billing or deductions.</p>
        </div>
      </div>

      <div className="df-actionbar">
        <Button variant="outline" onClick={() => navigate(`/money/report/${id}`)} className="flex-1 h-12 rounded-xl">Cancel</Button>
        <Button data-testid="edit-save" onClick={save} disabled={saving} className="flex-1 h-12 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] df-pressable">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </AppShell>
  );
}
